import _ from 'lodash'
import moment from 'moment'
import calendars from 'scoreboard/components/calendars'
import XRegExp from 'xregexp'

/**
 * Returns an object with the properties listed below. This method only
 * calculates the information needed for each style, so some fields may not
 * be fully populated.
 *
 * <ul>
 * <li>style - indicates how the period selector will handle the year
 * dropdown</li>
 * <li>yearsByPeriod - years keyed by period ID</li>
 * <li>periodsByYear - periods within a year keyed by that year</li>
 * <li>labelsByPeriod - label for the period keyed by period ID</li>
 * </ul>
 */

// 4 digits.  we don't want to look for 2 digit years b/c the numbers might
// refer to something else other than a year
var yearRegExp = /\d\d\d\d/g

// any number of characters that aren't words or hyphens (e.g. commas, spaces
// and other punction), 4 digits, and more non-word characters
var stripYearXRegExp = new XRegExp('[^\\w\\-\\p\{L\}]*\\d\\d\\d\\d[^\\w\\-\\p\{L\}]*', 'gu')

// cache of already calculated calendar info
var cache = {}
calendars.on('reset', function() {
	cache = {}
})

// calendar selector style indicating there shouldn't be a separate display
// for years.  for yearly and fiscal yearly calendars
var NO_YEARS = 'NO_YEARS'
// style indicating years should be based on calendar period labels, e.g.
// we know Q1 2009 is all in 2009
var YEARS_BY_LABEL = 'YEARS_BY_LABEL'
// style indicating years should be based on period start and end dates
// because a period could span multiple years, such as the week
// Dec 27, 2015 - Jan 2, 2016
var YEARS_BY_DATE = 'YEARS_BY_DATE'

// removes years from a period label, e.g.
// Dec 30, 2012 - Jan 5, 2013
// becomes
// Dec 30 - Jan 5, 2013
function removeYears(label) {
	return _.trim(XRegExp.replace(label, stripYearXRegExp, ' '))
}

function getYearMatches(label) {
	return label.match(yearRegExp)
}

function calendarInfo(calendar) {
	if (!calendar) {
		return null
	}

	if (!_.isUndefined(cache[calendar.id])) {
		return cache[calendar.id]
	}

	var info = {
		style: NO_YEARS,
		periodsByYear: {},
		labelsByPeriod: {}
	}

	// yearly and larger periods should not show a year dropdown, and don't
	// need all this extra fancy information
	if (calendar.avgPeriodSize < 365) {
		// assume we can use the YEARS_BY_LABEL style and start building up
		// the required information
		info.style = YEARS_BY_LABEL

		_.each(calendar.periods, function(period) {
			info.labelsByPeriod[period.id] = removeYears(period.label)

			var match = getYearMatches(period.label)
			// if there aren't years in the label, or multiple years we need
			// to go by date
			if (!match || match && match.length > 1) {
				info.style = YEARS_BY_DATE
			}
			// handle periods without years in their label
			if (!match) {
				var startYear = moment(period.start).year()
				var endYear = moment(period.end).year()
				addPeriod(info, startYear, period)
				if (startYear != endYear) {
					addPeriod(info, endYear, period)
				}
			}
			// for each year within the label, record the relevant info
			_.each(match, function(m) {
				var year = parseInt(m)
				addPeriod(info, year, period)
			})
		}.bind(this))
	}

	cache[calendar.id] = info

	return info
}

function addPeriod(info, year, period) {
	if (_.isUndefined(info.periodsByYear[year])) {
		info.periodsByYear[year] = []
	}
	info.periodsByYear[year].push(period)
}

_.extend(calendarInfo, {
	removeYears,
	getYearMatches,
	NO_YEARS,
	YEARS_BY_DATE,
	YEARS_BY_LABEL
})
export default calendarInfo
export { removeYears }
export { getYearMatches }
export { NO_YEARS }
export { YEARS_BY_DATE }
export { YEARS_BY_LABEL }

calendarInfo.__filename__ = 'components/calendar-info.js'
